import { useCallback, useState } from "react";
import toast from "react-hot-toast";
import axios, { AxiosError, AxiosResponse } from "axios";
import envConfig from "envConfig";
import { STORE_TIMING_APIS } from "@/api_framework/api_config";
import { IStoreTiming, IStoreTimingUpdatePayload } from "./interface";

interface IStoreTimingListResponse {
  status: boolean;
  message: string;
  data: IStoreTiming[];
}

const getAuthHeaders = () => ({
  Authorization: `${localStorage.getItem(envConfig.localStorage.ACCESS_TOKEN.key)}`,
  "Content-Type": "application/json",
});

const getErrorMessage = (error: unknown, fallback: string) =>
  (axios.isAxiosError(error) ? (error as AxiosError<{ message?: string }>).response?.data?.message : undefined) ||
  fallback;

export const useStoreTimingAPI = () => {
  const [storeTimingList, setStoreTimingList] = useState<IStoreTiming[]>([]);
  const [isListLoading, setIsListLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const fetchStoreTimingList = useCallback(async () => {
    setIsListLoading(true);
    try {
      const res: AxiosResponse<IStoreTimingListResponse> = await axios.get(STORE_TIMING_APIS.LIST, {
        headers: getAuthHeaders(),
      });
      if (res.data?.status) {
        setStoreTimingList(res.data.data ?? []);
      } else {
        toast.error(res.data?.message || "Failed to load quick commerce timings.");
      }
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to load quick commerce timings. Please try again."));
    } finally {
      setIsListLoading(false);
    }
  }, []);

  const saveStoreTiming = useCallback(
    async (warehouseId: string, payload: IStoreTimingUpdatePayload): Promise<boolean> => {
      setIsSaving(true);
      try {
        const res: AxiosResponse<{ status: boolean; message: string }> = await axios.post(
          `${STORE_TIMING_APIS.UPDATE_BY_WAREHOUSE}${warehouseId}/`,
          JSON.stringify(payload),
          { headers: getAuthHeaders() },
        );
        if (!res.data?.status) {
          toast.error(res.data?.message || "Failed to update quick commerce timing.");
          return false;
        }

        setStoreTimingList((prev) =>
          prev.map((timing) => (timing.warehouse_id === warehouseId ? { ...timing, ...payload } : timing)),
        );
        toast.success("Quick commerce timing updated.");
        return true;
      } catch (error) {
        toast.error(getErrorMessage(error, "Failed to update quick commerce timing. Please try again."));
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [],
  );

  return { storeTimingList, isListLoading, isSaving, fetchStoreTimingList, saveStoreTiming };
};
